/**
 * Ring Sizer Modal
 * 
 * Handles modal behavior, calibration, and measurement modes.
 * 
 * Lock OFF = Calibration Mode (slider controls coin size)
 * Lock ON  = Measurement Mode (slider controls ring size using UK chart)
 */

// Template coin size in millimeters
const coinTemplateSizeMm = 26;

// UK Ring Sizes (loaded inline from ring-sizes.json)
const ukRingSizes = [
  { "uk": "H", "us": "4", "diameter_mm": 14.8 },
  { "uk": "J", "us": "5", "diameter_mm": 15.7 },
  { "uk": "L", "us": "6", "diameter_mm": 16.5 },
  { "uk": "N", "us": "7", "diameter_mm": 17.3 },
  { "uk": "P", "us": "8", "diameter_mm": 18.1 },
  { "uk": "R", "us": "9", "diameter_mm": 19.0 },
  { "uk": "T", "us": "10", "diameter_mm": 19.8 }
];

class RingSizerModal {
  // Coin reference data
  static COINS = {
    'zar-r5': { diameter_mm: 26, label: 'R5 (26mm)' },
    'zar-r2': { diameter_mm: 23, label: 'R2 (23mm)' },
    'zar-r1': { diameter_mm: 20, label: 'R1 (20mm)' }
  };

  constructor() {
    this.modal = document.getElementById('ring-sizer-modal');
    this.coinTemplateSizeMm = coinTemplateSizeMm;
    this.overlay = document.getElementById('ring-sizer-modal-overlay');
    this.closeButton = document.getElementById('ring-sizer-modal-close');
    
    if (!this.modal || !this.overlay || !this.closeButton) {
      console.warn('Ring Sizer Modal: Required elements not found');
      return;
    }
    
    this.isOpen = false;
    this.lockIsActive = false;
    this.settingsActive = false; // Settings button state (measurement mode only)
    this.coinPanelOpen = false; // Coin panel state (calibration mode only)
    this.currentCoinKey = 'zar-r5'; // Default coin type
    
    // Available coins for lookup and UI management
    this.availableCoins = {
      'zar-r5': { diameter_mm: 26, name: 'R5' },
      'zar-r2': { diameter_mm: 23, name: 'R2' },
      'zar-r1': { diameter_mm: 20, name: 'R1' }
    };
    
    this.calibratedPxPerMm = null;
    this.currentRingIndex = null; // Ring size selection persists across mode switches
    this.currentCalibrationSizePx = null; // Calibration coin size persists across mode switches
    
    // Cache DOM elements
    this.slider = this.modal.querySelector('.ring-sizer-modal__slider');
    this.virtualCoin = this.modal.querySelector('.ring-sizer-modal__virtual-coin');
    this.virtualRing = this.modal.querySelector('.ring-sizer-modal__virtual-ring-canvas');
    this.calibrationCanvas = this.modal.querySelector('.ring-sizer-modal__coin-calibration-canvas');
    this.coinPanel = this.modal.querySelector('.ring-sizer-modal__coin-panel');
    
    this.init();
  }
  
  init() {
    // Close button click
    this.closeButton.addEventListener('click', (e) => {
      e.preventDefault();
      this.close();
    });
    
    // Overlay click (desktop only) - close modal
    this.overlay.addEventListener('click', (e) => {
      if (!this.isMobileDevice()) {
        this.close();
      }
    });
    
    // Prevent overlay click from bubbling to background
    this.modal.addEventListener('click', (e) => {
      e.stopPropagation();
    });
    
    // Keyboard: Escape key closes modal
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.isOpen) {
        this.close();
      }
    });
    
    // Lock button click handler
    this.setupLockButton();
    
    // Settings button click handler
    this.setupSettingsButton();
    
    // Coin option click handlers
    this.setupCoinOptions();
    
    // Slider input handler
    this.setupSlider();
    
    // Start in calibration mode
    this.enterCalibrationMode();
    
    // Set initial active coin UI and instructions
    this.updateActiveCoinUI(this.currentCoinKey);
    this.updateCalibrationInstructions(this.currentCoinKey);
  }
  
  // ── Settings Button ─────────────────────────────────
  
  setupSettingsButton() {
    const settingsButton = this.modal.querySelector('[aria-label="Settings"]');
    if (settingsButton) {
      settingsButton.addEventListener('click', (e) => {
        e.preventDefault();
        this.toggleSettings(settingsButton);
      });
    }
  }
  
  toggleSettings(button) {
    if (this.lockIsActive) {
      // Measurement mode: toggle on/off
      this.settingsActive = !this.settingsActive;
      
      if (this.settingsActive) {
        button.classList.add('is-active');
        this.modal.classList.add('settings-active');
      } else {
        button.classList.remove('is-active');
        this.modal.classList.remove('settings-active');
      }
    } else {
      // Calibration mode: toggle coin panel
      this.toggleCoinPanel();
    }
  }
  
  updateActiveCoinUI(key) {
    const coinOptions = this.modal.querySelectorAll('.ring-sizer-modal__coin-option');
    coinOptions.forEach(option => {
      const optionKey = option.getAttribute('data-coin');
      if (optionKey === key) {
        option.classList.add('is-active');
      } else {
        option.classList.remove('is-active');
      }
    });
  }
  
  updateCalibrationInstructions(key) {
    const coinData = this.availableCoins[key];
    if (!coinData) return;
    
    const instructionElement = this.modal.querySelector('[data-dynamic-coin]');
    if (instructionElement) {
      instructionElement.textContent = `Place a real ${coinData.name} coin on your screen.`;
    }
  }
  
  // ── Coin Panel (Calibration Mode) ───────────────────
  
  setupCoinOptions() {
    const coinOptions = this.modal.querySelectorAll('.ring-sizer-modal__coin-option');
    coinOptions.forEach(option => {
      option.addEventListener('click', (e) => {
        e.preventDefault();
        const coinKey = option.getAttribute('data-coin');
        this.selectCoin(coinKey);
      });
    });
  }
  
  toggleCoinPanel() {
    if (this.coinPanelOpen) {
      this.closeCoinPanel();
    } else {
      this.openCoinPanel();
    }
  }
  
  openCoinPanel() {
    if (!this.coinPanel || this.coinPanelOpen) return;
    
    this.coinPanelOpen = true;
    this.coinPanel.removeAttribute('hidden');
    this.modal.classList.add('coin-panel-active');

    const settingsButton = this.modal.querySelector('[aria-label="Settings"]');
    if (settingsButton) {
      settingsButton.classList.add('is-active');
    }
    
    // Position coin panel above settings button
    this.positionCoinPanel();
    
    // Add modal pointerdown listener to close panel on interaction start
    this.modal.addEventListener('pointerdown', this.handleOutsideClick);
  }
  
  positionCoinPanel() {
    if (!this.coinPanel) return;
    
    const settingsButton = this.modal.querySelector('[aria-label="Settings"]');
    if (!settingsButton) return;
    
    // Get settings button position relative to modal
    const buttonRect = settingsButton.getBoundingClientRect();
    const modalRect = this.modal.getBoundingClientRect();
    
    // Calculate position: left-aligned with settings button, above the control area
    const leftPos = buttonRect.left - modalRect.left;
    const bottomPos = modalRect.height - buttonRect.top + modalRect.top + 8; // 8px gap above button
    
    this.coinPanel.style.left = leftPos + 'px';
    this.coinPanel.style.bottom = bottomPos + 'px';
  }
  
  closeCoinPanel() {
    if (!this.coinPanel || !this.coinPanelOpen) return;
    
    this.coinPanelOpen = false;
    this.coinPanel.setAttribute('hidden', '');
    this.modal.classList.remove('coin-panel-active');

    const settingsButton = this.modal.querySelector('[aria-label="Settings"]');
    if (settingsButton && !this.settingsActive) {
      settingsButton.classList.remove('is-active');
    }
    
    // Remove modal pointerdown listener
    this.modal.removeEventListener('pointerdown', this.handleOutsideClick);
  }
  
  // Arrow function to preserve 'this' context for event listener
  handleOutsideClick = (e) => {
    // Don't close if clicking inside coin panel or on settings button
    const settingsButton = this.modal.querySelector('[aria-label="Settings"]');
    if (this.coinPanel.contains(e.target) || settingsButton.contains(e.target)) {
      return;
    }
    
    // Close panel on outside click
    this.closeCoinPanel();
  }
  
  selectCoin(key) {
    this.currentCoinKey = key;
    
    // Update coin template size based on selected coin
    if (this.availableCoins[key]) {
      this.coinTemplateSizeMm = this.availableCoins[key].diameter_mm;
    }
    
    // Update active coin UI and instructions
    this.updateActiveCoinUI(key);
    this.updateCalibrationInstructions(key);
    
    // Reset calibration
    this.calibratedPxPerMm = null;
    this.modal.style.removeProperty('--calibrated-px-per-mm');
    this.currentCalibrationSizePx = null;
    
    // Close coin panel
    this.closeCoinPanel();
    
    // Re-enter calibration mode to reset slider to midpoint
    this.enterCalibrationMode();
  }
  
  // ── Lock Button ──────────────────────────────────────
  
  setupLockButton() {
    const lockButton = this.modal.querySelector('[aria-label="Lock mode"]');
    if (lockButton) {
      lockButton.addEventListener('click', (e) => {
        e.preventDefault();
        this.toggleLock(lockButton);
      });
    }
  }
  
  toggleLock(button) {
    this.lockIsActive = !this.lockIsActive;
    
    if (this.lockIsActive) {
      button.classList.add('is-active');
      this.modal.classList.add('lock-active');
      
      // Close coin panel when entering measurement mode
      if (this.coinPanelOpen) {
        this.closeCoinPanel();
      }
      
      // Calculate calibration, then switch to measurement mode
      this.calculateCalibration();
      this.enterMeasurementMode();
    } else {
      button.classList.remove('is-active');
      this.modal.classList.remove('lock-active');
      
      // Switch back to calibration mode
      // Reset settings button state when exiting measurement mode
      this.settingsActive = false;
      const settingsButton = this.modal.querySelector('[aria-label="Settings"]');
      if (settingsButton) {
        settingsButton.classList.remove('is-active');
        this.modal.classList.remove('settings-active');
      }
      
      this.enterCalibrationMode();
    }
  }
  
  // ── Calibration ──────────────────────────────────────
  
  calculateCalibration() {
    if (!this.virtualCoin) return;
    
    const coinWidthPx = this.virtualCoin.offsetWidth;
    const calibratedPxPerMm = coinWidthPx / this.coinTemplateSizeMm;
    
    this.modal.style.setProperty('--calibrated-px-per-mm', calibratedPxPerMm);
    this.calibratedPxPerMm = calibratedPxPerMm;
  }
  
  // ── Slider ───────────────────────────────────────────
  
  setupSlider() {
    if (!this.slider) return;
    
    this.slider.addEventListener('input', () => {
      if (this.lockIsActive) {
        this.onMeasurementSliderInput();
      } else {
        this.onCalibrationSliderInput();
      }
    });
  }
  
  // ── Calibration Mode ─────────────────────────────────
  
  enterCalibrationMode() {
    if (!this.slider || !this.calibrationCanvas) return;
    
    // Get canvas width and coin min size from CSS custom property
    const canvasWidth = this.calibrationCanvas.offsetWidth;
    const coinMinSize = parseInt(
      getComputedStyle(this.calibrationCanvas).getPropertyValue('--coin-min-size'), 10
    ) || 40;
    
    const range = canvasWidth - coinMinSize;
    const stepValue = Math.max(1, Math.round(range * 0.02)); // 2% of total range
    
    // Update slider bounds (always recalculated)
    this.slider.min = coinMinSize;
    this.slider.max = canvasWidth;
    this.slider.step = stepValue;
    
    // Set currentCalibrationSizePx to midpoint on first entry
    if (this.currentCalibrationSizePx === null) {
      this.currentCalibrationSizePx = Math.round((coinMinSize + canvasWidth) / 2);
    }
    
    // Use stored calibration size (preserve across mode switches)
    this.slider.value = this.currentCalibrationSizePx;
    this.setCoinSize(this.currentCalibrationSizePx);
  }
  
  onCalibrationSliderInput() {
    const value = parseInt(this.slider.value, 10);
    // Persist the calibration coin size
    this.currentCalibrationSizePx = value;
    this.setCoinSize(value);
  }
  
  setCoinSize(sizePx) {
    if (!this.virtualCoin) return;
    this.virtualCoin.style.width = sizePx + 'px';
    this.virtualCoin.style.height = sizePx + 'px';
  }
  
  // ── Measurement Mode ─────────────────────────────────
  
  enterMeasurementMode() {
    if (!this.slider || !this.calibratedPxPerMm) return;
    
    const totalSizes = ukRingSizes.length;
    
    // Set currentRingIndex to midpoint on first entry
    if (this.currentRingIndex === null) {
      this.currentRingIndex = Math.floor(totalSizes / 2);
    }
    
    this.slider.min = 0;
    this.slider.max = totalSizes - 1;
    this.slider.step = 1;
    this.slider.value = this.currentRingIndex;
    
    // Apply ring size using stored index (recalculated with new calibration)
    this.setRingSize(this.currentRingIndex);
  }
  
  onMeasurementSliderInput() {
    const index = parseInt(this.slider.value, 10);
    // Persist the selected ring size
    this.currentRingIndex = index;
    this.setRingSize(index);
    
    // Toggle settings off when slider moves in measurement mode
    if (this.settingsActive) {
      this.settingsActive = false;
      const settingsButton = this.modal.querySelector('[aria-label="Settings"]');
      if (settingsButton) {
        settingsButton.classList.remove('is-active');
        this.modal.classList.remove('settings-active');
      }
    }
  }
  
  setRingSize(index) {
    if (!this.virtualRing || !this.calibratedPxPerMm) return;
    
    const sizeEntry = ukRingSizes[index];
    if (!sizeEntry) return;
    
    const diameterPx = sizeEntry.diameter_mm * this.calibratedPxPerMm;
    
    this.virtualRing.style.width = diameterPx + 'px';
    
    // Update diameter measurement label
    const diameterLabel = this.virtualRing.querySelector('[data-ring-diameter]');
    if (diameterLabel) {
      diameterLabel.textContent = sizeEntry.diameter_mm.toFixed(1) + ' mm';
    }
    
    // Update size display data attributes with fade effect
    const ukSizeElement = this.modal.querySelector('[data-size-uk]');
    const usSizeElement = this.modal.querySelector('[data-size-us]');
    
    if (ukSizeElement) {
      // Add fade-out class
      ukSizeElement.classList.add('size-updating');
      
      // Update text and remove fade-out class to trigger fade-in
      setTimeout(() => {
        ukSizeElement.textContent = sizeEntry.uk;
        ukSizeElement.classList.remove('size-updating');
      }, 0);
    }
    
    if (usSizeElement) {
      // Add fade-out class
      usSizeElement.classList.add('size-updating');
      
      // Update text and remove fade-out class to trigger fade-in
      setTimeout(() => {
        usSizeElement.textContent = sizeEntry.us;
        usSizeElement.classList.remove('size-updating');
      }, 0);
    }
  }
  
  // ── Modal Open/Close ─────────────────────────────────
  
  open() {
    if (this.isOpen) return;
    
    // Always reset to calibration mode when opening
    if (this.lockIsActive) {
      this.lockIsActive = false;
      const lockButton = this.modal.querySelector('[aria-label="Lock mode"]');
      if (lockButton) {
        lockButton.classList.remove('is-active');
      }
      this.modal.classList.remove('lock-active');
      
      // Reset settings state if active
      this.settingsActive = false;
      const settingsButton = this.modal.querySelector('[aria-label="Settings"]');
      if (settingsButton) {
        settingsButton.classList.remove('is-active');
        this.modal.classList.remove('settings-active');
      }
    }
    
    this.isOpen = true;
    this.modal.classList.add('is-active');
    this.overlay.classList.add('is-active');
    
    document.body.classList.add('ring-sizer-modal-open');
    
    requestAnimationFrame(() => {
      this.modal.focus();
      // Enter calibration mode with recalculated bounds
      this.enterCalibrationMode();
    });
  }
  
  close() {
    if (!this.isOpen) return;
    
    // Close coin panel if open
    if (this.coinPanelOpen) {
      this.closeCoinPanel();
    }
    
    this.isOpen = false;
    this.modal.classList.remove('is-active');
    this.overlay.classList.remove('is-active');
    
    document.body.classList.remove('ring-sizer-modal-open');
  }
  
  isMobileDevice() {
    return window.innerWidth <= 768;
  }
  
  toggle() {
    this.isOpen ? this.close() : this.open();
  }
}

// Initialize modal
const ringSizerModal = new RingSizerModal();

// Expose global for debugging
window.ringSizerModal = ringSizerModal;

document.addEventListener('click', (event) => {
  const trigger = event.target.closest('a[href="#ring-sizer"]');
  if (!trigger) return;

  event.preventDefault();
  ringSizerModal.open();
});
